import { CheckCircle, XCircle } from '@untitledui/icons';
import type { ReactNode } from 'react';

import {
  DEFAULT_FATHOM_ERROR_MESSAGE,
  DEFAULT_FIREFLIES_ERROR_MESSAGE,
  DEFAULT_GRANOLA_ERROR_MESSAGE,
  FATHOM_ERROR_MESSAGES,
  FIREFLIES_ERROR_MESSAGES,
  GRANOLA_ERROR_MESSAGES,
} from '@/lib/integrations/meeting-summaries/error-messages';

export interface OAuthBannersProps {
  fathomSuccess: boolean;
  fathomError: string | null;
  granolaSuccess: boolean;
  granolaError: string | null;
  firefliesError: string | null;
  onDismissFathomSuccess: () => void;
  onDismissFathomError: () => void;
  onDismissGranolaSuccess: () => void;
  onDismissGranolaError: () => void;
  onDismissFirefliesError: () => void;
}

interface OAuthBannerProps {
  type: 'success' | 'error';
  title: string;
  description: string | ReactNode;
  onDismiss: () => void;
  ariaLabel: string;
}

const SUCCESS_CLASSES = {
  container: 'bg-utility-success-50 border-utility-success-200',
  icon: 'text-utility-success-600',
  title: 'text-utility-success-900',
  description: 'text-utility-success-700',
  dismiss: 'text-utility-success-600 hover:text-utility-success-700',
};

const ERROR_CLASSES = {
  container: 'bg-utility-error-50 border-utility-error-200',
  icon: 'text-utility-error-600',
  title: 'text-utility-error-900',
  description: 'text-utility-error-700',
  dismiss: 'text-utility-error-600 hover:text-utility-error-700',
};

function OAuthBanner({
  type,
  title,
  description,
  onDismiss,
  ariaLabel,
}: OAuthBannerProps) {
  const isSuccess = type === 'success';
  const classes = isSuccess ? SUCCESS_CLASSES : ERROR_CLASSES;
  const Icon = isSuccess ? CheckCircle : XCircle;
  const role = isSuccess ? 'status' : 'alert';

  return (
    <div role={role} className={`p-4 rounded-lg border ${classes.container}`}>
      <div className='flex items-start gap-3'>
        <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${classes.icon}`} />
        <div className='flex-1'>
          <p className={`text-sm font-semibold ${classes.title}`}>{title}</p>
          <p className={`text-sm mt-1 ${classes.description}`}>{description}</p>
        </div>
        <button
          type='button'
          aria-label={ariaLabel}
          onClick={onDismiss}
          className={`flex-shrink-0 ${classes.dismiss}`}
        >
          <XCircle aria-hidden='true' className='w-5 h-5' />
        </button>
      </div>
    </div>
  );
}

export function OAuthBanners({
  fathomSuccess,
  fathomError,
  granolaSuccess,
  granolaError,
  firefliesError,
  onDismissFathomSuccess,
  onDismissFathomError,
  onDismissGranolaSuccess,
  onDismissGranolaError,
  onDismissFirefliesError,
}: OAuthBannersProps) {
  return (
    <>
      {fathomSuccess && (
        <OAuthBanner
          type='success'
          title='Fathom connected successfully'
          description='Your Fathom account is now connected to capture meeting notes.'
          onDismiss={onDismissFathomSuccess}
          ariaLabel='Dismiss Fathom connected notification'
        />
      )}
      {fathomError && (
        <OAuthBanner
          type='error'
          title="We couldn't connect Fathom"
          description={
            Object.hasOwn(FATHOM_ERROR_MESSAGES, fathomError)
              ? FATHOM_ERROR_MESSAGES[fathomError]
              : DEFAULT_FATHOM_ERROR_MESSAGE
          }
          onDismiss={onDismissFathomError}
          ariaLabel='Dismiss Fathom connection error'
        />
      )}
      {granolaSuccess && (
        <OAuthBanner
          type='success'
          title='Granola connected successfully'
          description='Your Granola account is now connected to query meeting notes and transcripts.'
          onDismiss={onDismissGranolaSuccess}
          ariaLabel='Dismiss Granola connected notification'
        />
      )}
      {granolaError && (
        <OAuthBanner
          type='error'
          title="We couldn't connect Granola"
          description={
            Object.hasOwn(GRANOLA_ERROR_MESSAGES, granolaError)
              ? GRANOLA_ERROR_MESSAGES[granolaError]
              : DEFAULT_GRANOLA_ERROR_MESSAGE
          }
          onDismiss={onDismissGranolaError}
          ariaLabel='Dismiss Granola connection error'
        />
      )}
      {firefliesError && (
        <OAuthBanner
          type='error'
          title="We couldn't connect Fireflies"
          description={
            Object.hasOwn(FIREFLIES_ERROR_MESSAGES, firefliesError)
              ? FIREFLIES_ERROR_MESSAGES[firefliesError]
              : DEFAULT_FIREFLIES_ERROR_MESSAGE
          }
          onDismiss={onDismissFirefliesError}
          ariaLabel='Dismiss Fireflies connection error'
        />
      )}
    </>
  );
}
